import RadioListField from '@acorn-portal/shared/src/components-v2/inputs/radio-list-field';
import SelectField from '@acorn-portal/shared/src/components-v2/inputs/select-field';
import TimeFieldGroup from '@acorn-portal/shared/src/components-v2/inputs/time-field-group';
import { useTimeDropdownOptions } from '@acorn-portal/shared/src/hooks/use-time-dropdown-options';
import { MinuteIncrement } from '@acorn-portal/shared/src/hooks/use-time-dropdown-options/types';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button } from '@mui/material';
import { FormikConfig, FormikContextType, useFormik } from 'formik';
import { Link } from 'react-router';
import ActionButtonWrapper from '../../components/action-button-wrapper';
import { CancellationDateDescription } from '../../types';
import { StaticDropdownOptions } from '../../utils/form-dropdowns/date-form-dropdowns/get-static-dropdown-options';
import { CancellationDateFormValues } from '../../utils/get-initial-cancellation-date-form-values';
import { useFormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IProps {
	initialValues: CancellationDateFormValues;
	validationSchema: FormikConfig<CancellationDateFormValues>['validationSchema'];
	staticOptions: StaticDropdownOptions;
	timeDropDownConfig?: { minuteIncrement?: MinuteIncrement; hidePastTimes?: boolean };
	handleSubmit: (values: CancellationDateFormValues) => void;
	onBack?: () => void;
}

const CancellationDateForm = ({
	initialValues,
	validationSchema,
	staticOptions,
	timeDropDownConfig = {},
	handleSubmit,
	onBack,
}: IProps) => {
	// Initialize formik
	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: handleSubmit,
	});

	const { minuteIncrement = 15, hidePastTimes = false } = timeDropDownConfig;

	const timeOptions = useTimeDropdownOptions({
		minuteIncrement,
		hidePastTimes,
		selectedDate: hidePastTimes ? formik.values.cancellationDate : undefined,
		selectedHour: hidePastTimes ? formik.values.cancellationTime?.hours : undefined,
	});

	useFormikLogErrors(formik as FormikContextType<unknown>);

	return (
		<form id='cancellation-date-form' data-test-id='cancellation-date-form' onSubmit={formik.handleSubmit}>
			{/* DESCRIPTION */}
			<Box sx={{ mb: '40px' }}>
				<RadioListField
					id='cancellation-date-description'
					label='When do you want your policy to be cancelled from?'
					options={staticOptions.description}
					value={formik.values.cancellationDateDescription}
					onChange={(value) => {
						formik.resetForm({
							values: {
								cancellationDateDescription: value as CancellationDateDescription,
								cancellationDate: undefined,
								cancellationTime: undefined,
							},
						});
					}}
					hasError={!!formik.errors.cancellationDateDescription && formik.touched.cancellationDateDescription}
					helperText={formik.touched.cancellationDateDescription ? formik.errors.cancellationDateDescription : ''}
					layout='horizontal'
				/>
			</Box>

			{formik.values.cancellationDateDescription === 'NOW' && (
				<Alert severity='info' variant='outlined' sx={{ borderRadius: '4px' }}>
					Once confirmed, your policy will be cancelled within 15 minutes.
				</Alert>
			)}
			{/* Only show these fields if 'LATER' is selected */}
			{formik.values.cancellationDateDescription === 'LATER' && (
				<>
					<Box sx={{ mb: '40px' }}>
						{/* DATE */}
						<SelectField
							label='What date would you like to cancel?'
							description='You can select a date up to 7 days in the future.'
							placeholder='Select a date'
							id='cancellation-date'
							name='cancellationDate'
							options={staticOptions.date}
							value={formik.values.cancellationDate}
							hasError={!!formik.errors.cancellationDate && formik.touched.cancellationDate}
							errorText={formik.touched.cancellationDate ? formik.errors.cancellationDate : ''}
							onChange={(e) => {
								formik.setFieldValue('cancellationDate', e.target.value);
								if (hidePastTimes) formik.setFieldValue('cancellationTime', { hours: '', minutes: '' }); // If hiding past times, it's safer to reset the time
							}}
						/>
					</Box>
					{/* HOURS & MINUTES */}
					<Box sx={{ mb: '40px' }}>
						<TimeFieldGroup
							id='cancellation-time'
							legend='What time would you like to cancel?'
							hasError={!!formik.errors.cancellationTime && formik.touched.cancellationTime}
							errorText={formik.touched.cancellationTime ? formik.errors.cancellationTime : ''}
							hourInputProps={{
								options: timeOptions.hours,
								value: formik.values.cancellationTime?.hours ?? '',
								onChange: (e) =>
									formik.setFieldValue('cancellationTime', {
										hours: e.target.value,
										minutes: hidePastTimes ? '' : (formik.values.cancellationTime?.minutes ?? ''), // If hiding past times, recalculations required on available minutes
									}),
							}}
							minuteInputProps={{
								options: timeOptions.minutes,
								value: formik.values.cancellationTime?.minutes ?? '',
								onChange: (e) =>
									formik.setFieldValue('cancellationTime', {
										...formik.values.cancellationTime,
										minutes: e.target.value,
									}),
							}}
						/>
					</Box>
				</>
			)}
			<Box sx={{ mt: '64px' }}>
				<ActionButtonWrapper>
					<Button
						startIcon={<ArrowBack />}
						component={Link}
						to={ROUTE_PATHS.cancellationReason}
						variant='text'
						size='medium'
						sx={{ color: 'primary.dark' }}
						onClick={() => onBack?.()}
					>
						Go back
					</Button>
					<Button size='medium' variant='contained' type='submit'>
						Continue to confirmation
					</Button>
				</ActionButtonWrapper>
			</Box>
		</form>
	);
};

export default CancellationDateForm;
